import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogContent, Box, Typography, LinearProgress,
  Button, TextField, Grid, FormControl, FormLabel, RadioGroup,
  FormControlLabel, Radio, Checkbox, Divider, IconButton,
  Chip, Alert, InputAdornment, Select, MenuItem, InputLabel,
  Paper, Fade, CircularProgress, Tooltip,
  Stepper, Step, StepLabel, StepContent as MuiStepContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LockIcon from '@mui/icons-material/Lock';
import VerificadorDocumento from './VerificadorDocumento';
import DrawIcon from '@mui/icons-material/Draw';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { translations, defaultLanguage } from '../data/Translations';
import {
  getPaises, getDepartamentos, getMunicipios,
  verificarEmail, verificarDocumento, geocodificarDireccion, registrarAfiliacion,
  getCooperativasPublicas,
} from '../services/ecolabApi';
import { descargarContratoPDF } from '../utils/generarContratoPDF';

// ── Colores Capellanías ──────────────────────────────────────────────────────
const C = {
  gold:     '#D4AF37',
  darkGold: '#B8960C',
  lightGold:'#F4E4BC',
  black:    '#1A1A1A',
  darkGrey: '#2D2D2D',
  cream:    '#FDF8E8',
  offWhite: '#F8F6F0',
};

// ── Helpers de UI ────────────────────────────────────────────────────────────
const SectionText = ({ children, sx = {} }) => (
  <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.8, fontSize: '0.82rem', textAlign: 'justify', mb: 1.5, ...sx }}>
    {children}
  </Typography>
);

const ClausulaTitle = ({ number, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 3 }}>
    <Chip label={number} size="small" sx={{ background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, color: C.gold, fontWeight: 700, fontSize: '0.7rem', height: 24 }} />
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.black, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</Typography>
  </Box>
);

const ReadSection = ({ title, children, onRead, read, btnLeer = 'Leer y Aceptar', btnLeido = '✓ Leído y aceptado' }) => (
  <Paper elevation={0} sx={{ border: read ? `1.5px solid ${C.gold}` : '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 2, background: read ? `rgba(212,175,55,0.06)` : '#fafafa', transition: 'all 0.3s ease' }}>
    {title && <ClausulaTitle {...title} />}
    {children}
    {onRead && (
      <Box sx={{ mt: 1.5 }}>
        {!read && (
          <Typography variant="body2" sx={{ color: C.darkGold, fontStyle: 'italic', fontWeight: 700, mb: 1 }}>
            Si ha leído el anterior texto y acepta las condiciones, de clic en el siguiente botón.
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" variant={read ? 'contained' : 'outlined'} onClick={onRead}
            startIcon={read ? <CheckCircleOutlineIcon /> : null}
            sx={{
              borderRadius: 6, fontSize: '0.75rem',
              background: read ? `linear-gradient(135deg, ${C.black}, ${C.darkGrey})` : 'transparent',
              color: read ? C.gold : C.darkGold,
              borderColor: C.gold,
              '&:hover': { background: read ? C.darkGrey : `rgba(212,175,55,0.08)`, borderColor: C.darkGold },
            }}>
            {read ? btnLeido : btnLeer}
          </Button>
        </Box>
      </Box>
    )}
  </Paper>
);

const DateField = ({ label, value, onChange, required, error, helperText, maxYear, minYear }) => (
  <TextField fullWidth label={label} required={required} size="small" type="date" value={value}
    onChange={e => onChange(e.target.value)} error={error} helperText={helperText}
    InputLabelProps={{ shrink: true }}
    inputProps={{ max: `${maxYear || new Date().getFullYear()}-12-31`, min: `${minYear || 1920}-01-01` }} />
);

function newCaptcha() {
  const ops = ['+', '-', '×'];
  const op = ops[Math.floor(Math.random() * 3)];
  let a, b, r;
  if (op === '+') { a = Math.floor(Math.random() * 15) + 1; b = Math.floor(Math.random() * 15) + 1; r = a + b; }
  else if (op === '-') { a = Math.floor(Math.random() * 15) + 5; b = Math.floor(Math.random() * a) + 1; r = a - b; }
  else { a = Math.floor(Math.random() * 9) + 2; b = Math.floor(Math.random() * 9) + 2; r = a * b; }
  return { a, b, op, r };
}

// STEPS se construye dinámicamente desde las traducciones en el componente

const INIT = {
  cooperativaId: '', nombre: '', apellidos: '', cedula: '', cedulaExpedida: '',
  fechaNacimiento: '', genero: 'M', estadoCivil: '',
  paisId: 1045, departamentoId: '', municipioId: '',
  direccion: '', codigoPostal: '', celular: '', celular2: '', email: '', codigoAlfaNum: '',
  beneficiarios: [
    { nombres: '', apellidos: '', cedula: '', contacto: '' },
    { nombres: '', apellidos: '', cedula: '', contacto: '' },
    { nombres: '', apellidos: '', cedula: '', contacto: '' },
  ],
  modalidadAporte: 'DINERO', fechaPrimeraCuota: '', valorBienes: '',
  readClauses: {},
  aceptaTerminos: false, declaracionVeracidad: false, autorizaDatos: false,
  captchaResp: '',
};

export default function ContratoAfiliacion({ open, onClose, onSuccess, cooperativaIdFijo = null }) {
  const [lang, setLang] = useState(() => {
    const s = localStorage.getItem('preferredLanguage');
    return (s && translations[s]) ? s : defaultLanguage;
  });
  useEffect(() => {
    const handler = (e) => { if (e.key === 'preferredLanguage' && e.newValue && translations[e.newValue]) setLang(e.newValue); };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);
  const tc = translations[lang]?.contrato || translations[defaultLanguage].contrato;
  const STEPS = tc.steps.map(label => ({ label }));

  const [step, setStep] = useState(0);
  const [docIdentidad,    setDocIdentidad]    = useState(null);
  const [urlDocIdentidad, setUrlDocIdentidad] = useState(null);
  const [docsPaso,        setDocsPaso]        = useState(0);
  const [docsAdicionales, setDocsAdicionales] = useState({
    rut:                      { file: null, url: null },
    antecedentes_procuraduria:{ file: null, url: null },
    antecedentes_contraloria: { file: null, url: null },
    antecedentes_policia:     { file: null, url: null },
    bancario:                 { file: null, url: null },
  });

  const scrollToDoc = (idx) => {
    setTimeout(() => {
      const el = document.getElementById(`doc-step-${idx}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const setDoc = (tipo, file, url) =>
    setDocsAdicionales(p => ({ ...p, [tipo]: { ...p[tipo], file, url } }));

  const [docsPendientes, setDocsPendientes] = useState({
    rut: false, antecedentes_procuraduria: false,
    antecedentes_contraloria: false, antecedentes_policia: false, bancario: false,
  });
  const marcarPendiente  = (tipo) => setDocsPendientes(p => ({ ...p, [tipo]: true }));
  const desmarcarPendiente = (tipo) => setDocsPendientes(p => ({ ...p, [tipo]: false }));
  const docResuelto      = (tipo) => !!docsAdicionales[tipo]?.url || docsPendientes[tipo];
  const todosDocsResueltos = ['rut','antecedentes_procuraduria','antecedentes_contraloria','antecedentes_policia','bancario']
    .every(t => docResuelto(t));

  const [form, setForm] = useState(INIT);
  const [cooperativas, setCooperativas] = useState([]);
  const [coopSeleccionada, setCoopSeleccionada] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [afiliadoId, setAfiliadoId] = useState(null);
  const [hashContrato, setHashContrato] = useState(null);
  const [pdfCargando, setPdfCargando] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const contentRef = useRef(null);

  const [paises, setPaises] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError,   setGeoError]   = useState('');
  const [captcha, setCaptcha] = useState(() => newCaptcha());
  const [emailStatus,       setEmailStatus]       = useState('idle');
  const [cuentasExistentes, setCuentasExistentes] = useState([]);
  const [cuentaElegida,     setCuentaElegida]     = useState(null);
  const [cedulaStatus, setCedulaStatus] = useState('idle');

  useEffect(() => {
    if (open) {
      getPaises().then(d => setPaises(d || [])).catch(() => {});
      getCooperativasPublicas().then(d => {
        setCooperativas(d || []);
        if (cooperativaIdFijo) {
          const c = (d || []).find(x => x.cooperativaID === cooperativaIdFijo || x.cooperativaID === Number(cooperativaIdFijo));
          if (c) setCoopSeleccionada(c);
        }
      }).catch(() => {});
      if (cooperativaIdFijo) setForm(f => ({ ...f, cooperativaId: cooperativaIdFijo }));
    }
  }, [open, cooperativaIdFijo]);

  useEffect(() => {
    if (!form.paisId) { setDepartamentos([]); return; }
    setLoadingGeo(true);
    getDepartamentos(form.paisId).then(d => setDepartamentos(d || [])).catch(() => setDepartamentos([])).finally(() => setLoadingGeo(false));
  }, [form.paisId]);

  useEffect(() => {
    if (!form.departamentoId) { setMunicipios([]); return; }
    setLoadingGeo(true);
    getMunicipios(form.departamentoId).then(d => setMunicipios(d || [])).catch(() => setMunicipios([])).finally(() => setLoadingGeo(false));
  }, [form.departamentoId]);

  useEffect(() => {
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setEmailStatus('idle'); return; }
    const t = setTimeout(() => {
      setEmailStatus('checking');
      verificarEmail(form.email, cooperativaIdFijo || form.cooperativaId || undefined)
        .then(d => setEmailStatus(d.disponible ? 'ok' : 'taken'))
        .catch(() => setEmailStatus('idle'));
    }, 800);
    return () => clearTimeout(t);
  }, [form.email, form.cooperativaId]);

  useEffect(() => {
    if (!form.cedula || form.cedula.trim().length < 5) { setCedulaStatus('idle'); return; }
    const t = setTimeout(() => {
      setCedulaStatus('checking');
      setCuentasExistentes([]); setCuentaElegida(null);
      verificarDocumento(form.cedula.trim(), cooperativaIdFijo || form.cooperativaId || undefined)
        .then(d => {
          if (d.excluido) { setCedulaStatus('excluido'); }
          else if (!d.disponible) { setCedulaStatus('taken'); }
          else {
            setCedulaStatus('ok');
            if (d.cuentasExistentes?.length > 0) setCuentasExistentes(d.cuentasExistentes);
          }
        }).catch(() => setCedulaStatus('idle'));
    }, 700);
    return () => clearTimeout(t);
  }, [form.cedula, form.cooperativaId]);

  useEffect(() => { setCuentasExistentes([]); setCuentaElegida(null); }, [form.cedula]);
  useEffect(() => { if (contentRef.current) contentRef.current.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

  const sf = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const rc = (k) => setForm(p => ({ ...p, readClauses: { ...p.readClauses, [k]: new Date().toISOString() } }));
  const sb = (i, f, v) => { const u = [...form.beneficiarios]; u[i] = { ...u[i], [f]: v }; setForm(p => ({ ...p, beneficiarios: u })); };

  const handleGeo = useCallback(async () => {
    if (!form.paisId || !form.departamentoId || !form.municipioId) { setGeoError('Seleccione País, Departamento y Ciudad/Municipio antes de obtener el código postal.'); return; }
    if (!form.direccion.trim()) { setGeoError('Ingrese la dirección para obtener el código postal.'); return; }
    setGeoError(''); setGeoLoading(true);
    const pid = String(form.paisId), did = String(form.departamentoId), mid = String(form.municipioId);
    const pais   = paises.find(p => String(p.PaisID||p.paisID) === pid)?.Nombre || '';
    const depto  = departamentos.find(d => String(d.DepartamentoID||d.departamentoID) === did)?.Nombre || '';
    const ciudad = municipios.find(m => String(m.MunicipioID||m.municipioID) === mid)?.Nombre || '';
    const geoResult = await geocodificarDireccion(form.direccion.trim(), ciudad, depto, pais);
    if (geoResult.codigoPostal) { sf('codigoPostal', geoResult.codigoPostal); setGeoError(''); }
    else { setGeoError('No se encontró código postal automáticamente. Puede ingresarlo manualmente.'); }
    setGeoLoading(false);
  }, [form.direccion, form.paisId, form.departamentoId, form.municipioId, municipios, departamentos, paises]);

  const validate = () => {
    const e = {};
    if (step === 0 && !form.readClauses['intro']) e.rc = 'Debe leer y aceptar la cláusula para continuar.';
    if (step === 1) {
      if (!form.nombre.trim())    e.nombre    = 'Requerido';
      if (!form.apellidos.trim()) e.apellidos = 'Requerido';
      if (!form.cedula.trim())    e.cedula    = 'Requerido';
      else if (cedulaStatus === 'taken')    e.cedula = 'Esta cédula ya tiene una solicitud activa en esta cooperativa.';
      else if (cedulaStatus === 'checking') e.cedula = 'Verificando cédula…';
      else if (cedulaStatus === 'excluido') e.cedula = 'En este momento no es posible completar su solicitud. Para más información contáctenos directamente.';
      if (!form.celular.trim())   e.celular   = 'Requerido';
      if (!form.email.trim())     e.email     = 'Requerido';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email inválido';
      else if (emailStatus === 'taken')    e.email = 'Este correo ya está registrado en esta cooperativa.';
      else if (emailStatus === 'checking') e.email = 'Verificando disponibilidad del correo…';
      if (!form.estadoCivil)     e.estadoCivil = 'Requerido';
      if (!form.fechaNacimiento) e.fechaNac  = 'Requerido';
    }
    if (step === 2) {
      if (!docIdentidad)         e.rc = 'Debe subir su Cédula de Ciudadanía para continuar.';
      else if (!urlDocIdentidad) e.rc = 'La cédula debe pasar la verificación automática (OCR) antes de continuar.';
      else if (!todosDocsResueltos) e.rc = 'Debe subir o marcar como pendiente cada uno de los documentos adicionales.';
    }
    if (step === 5 && Object.keys(form.readClauses).filter(k => k.startsWith('ob')).length < 3) e.rc = 'Debe leer y aceptar todas las cláusulas.';
    if (step === 6 && !form.readClauses['conf']) e.rc = 'Debe aceptar la cláusula de confidencialidad.';
    if (step === 7) {
      if (!form.aceptaTerminos)       e.at = 'Requerido';
      if (!form.declaracionVeracidad) e.dv = 'Requerido';
      if (!form.autorizaDatos)        e.ad = 'Requerido';
      if (parseInt(form.captchaResp, 10) !== captcha.r) e.captcha = 'Respuesta incorrecta';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const stepListo = useMemo(() => {
    switch (step) {
      case 0: return !!form.readClauses['intro'];
      case 1: return (
        (!!cooperativaIdFijo || !!form.cooperativaId) &&
        !!form.nombre.trim() && !!form.apellidos.trim() &&
        !!form.cedula.trim() && cedulaStatus === 'ok' && !!form.celular.trim() &&
        !!form.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
        emailStatus === 'ok' && (cuentasExistentes.length === 0 || cuentaElegida !== null) &&
        !!form.estadoCivil && !!form.fechaNacimiento
      );
      case 2: return !!urlDocIdentidad && todosDocsResueltos;
      case 3: return !!form.readClauses['incorp'];
      case 4: return !!form.readClauses['obj5'];
      case 5: return ['ob7','ob8','ob9'].every(k => !!form.readClauses[k]);
      case 6: return !!form.readClauses['conf'];
      case 7: return !!form.aceptaTerminos && !!form.declaracionVeracidad && !!form.autorizaDatos &&
        parseInt(form.captchaResp, 10) === captcha.r;
      default: return true;
    }
  }, [step, form, cedulaStatus, emailStatus, urlDocIdentidad, todosDocsResueltos, captcha.r, cuentasExistentes.length, cuentaElegida]);

  const handleNext = () => { if (validate()) { if (step < 7) setStep(s => s + 1); else handleSubmit(); } };
  const handleBack = () => { setErrors({}); setStep(s => Math.max(0, s - 1)); };

  const handleSubmit = async () => {
    setSubmitting(true); setSubmitError('');
    try {
      const respuesta = await registrarAfiliacion({
        nombres: form.nombre.trim(), apellidos: form.apellidos.trim(),
        tipoDocumento: 'CC', documento: form.cedula.trim(),
        documentoExpedidoEn: form.cedulaExpedida.trim() || null,
        fechaNacimiento: form.fechaNacimiento || null,
        genero: form.genero, estadoCivil: form.estadoCivil?.toUpperCase() || null,
        codigoAlfaNumerico: form.codigoAlfaNum.trim() || null,
        email: form.email.trim(), celular: form.celular.trim(), celular2: form.celular2.trim() || null,
        paisId: form.paisId || null, departamentoId: form.departamentoId || null, municipioId: form.municipioId || null,
        direccion: form.direccion.trim() || null, codigoPostal: form.codigoPostal.trim() || null,
        modalidadAporte: form.modalidadAporte, fechaPrimeraCuota: form.fechaPrimeraCuota || null,
        beneficiarios: form.beneficiarios.filter(b => b.nombres.trim() && b.apellidos.trim()).map(b => ({
          nombres: b.nombres.trim(), apellidos: b.apellidos.trim(), documento: b.cedula.trim() || null, contacto: b.contacto.trim() || null,
        })),
        urlDocIdentidad: urlDocIdentidad || null,
        cooperativaId: form.cooperativaId ? Number(form.cooperativaId) : null,
        aceptacionesClausulas: Object.entries(form.readClauses).map(([clausula, timestamp]) => ({ clausula, timestamp })),
        urlDocRut:                     docsAdicionales.rut?.url || null,
        urlDocAntecedentesProcu:       docsAdicionales.antecedentes_procuraduria?.url || null,
        urlDocAntecedentesContraloria: docsAdicionales.antecedentes_contraloria?.url || null,
        urlDocAntecedentesPolicia:     docsAdicionales.antecedentes_policia?.url || null,
        urlDocBancario:                docsAdicionales.bancario?.url || null,
        docsPendientes: Object.entries(docsPendientes).filter(([, v]) => v).map(([k]) => k),
      });
      const afiliadoIdResp = respuesta?.afiliadoID || null;
      setAfiliadoId(afiliadoIdResp);
      setHashContrato(respuesta?.hashContrato || null);
      setSubmitted(true);
      onSuccess?.({ cedula: form.cedula.trim(), nombres: form.nombre.trim(), apellidos: form.apellidos.trim(), estado: 'PENDIENTE', afiliadoId: afiliadoIdResp });
    } catch (err) {
      setSubmitError(err?.data?.mensaje || err.message || 'Error al enviar. Intente nuevamente.');
    } finally { setSubmitting(false); }
  };

  const entidadPDF = coopSeleccionada ? {
    nombre: coopSeleccionada.nombre || 'ALIANZA SOLIDARIA DE CAPELLANES',
    siglas: coopSeleccionada.siglas || 'ASC',
    tipoEntidad: coopSeleccionada.tipoEntidad || 'ALIANZA',
    nit: coopSeleccionada.nit || '',
    representante: coopSeleccionada.representanteLegal || '',
    cedulaRep: coopSeleccionada.cedulaRepresentante || '',
    direccion: coopSeleccionada.direccion || '',
    telefono: coopSeleccionada.telefono || '',
    email: coopSeleccionada.email || '',
    sitioWeb: coopSeleccionada.sitioWeb || '',
    cuentaBancaria: coopSeleccionada.cuentaBancaria || '',
  } : null;

  // ── STEP 0 — Partes del Contrato ─────────────────────────────────────────
  const S0 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700, mb: 0.5 }}>Contrato de Inscripción a la Alianza</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Lea cuidadosamente antes de avanzar.</Typography>
      <ReadSection title={tc.s0.clausula} onRead={() => rc('intro')} read={!!form.readClauses['intro']} btnLeer={tc.s0.btnLeer} btnLeido={tc.s0.btnLeido}>
        <SectionText>{tc.s0.p1}</SectionText>
        <SectionText>{tc.s0.p2}</SectionText>
        <SectionText>{tc.s0.p3}</SectionText>
      </ReadSection>
      {errors.rc && <Alert severity="warning">{errors.rc}</Alert>}
    </Box>
  );

  // ── STEP 1 — Datos Personales ────────────────────────────────────────────
  const S1 = () => {
    const W = { width: '100%' };
    const half = { width: 'calc(50% - 8px)' };
    const third = { width: 'calc(33.33% - 11px)' };
    const twoThird = { width: 'calc(66.66% - 5px)' };
    const row = { display: 'flex', flexWrap: 'wrap', gap: '16px', mb: 2 };
    return (
    <Box>
      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700, mb: 0.5 }}>Datos del Capellán Asociado</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Complete la información personal del Capellán que se inscribe.</Typography>
      <ClausulaTitle number="CLÁUSULA 2ª" title="Formato de Inscripción" />

      {/* Fila 1: Entidad sola */}
      <Box sx={row}>
        <FormControl size="small" sx={W} required error={!!errors.cooperativaId} disabled={!!cooperativaIdFijo}>
          <InputLabel>Entidad a la que se inscribe *</InputLabel>
          <Select value={form.cooperativaId} label="Entidad a la que se inscribe *"
            onChange={e => { sf('cooperativaId', e.target.value); const c = cooperativas.find(x => x.cooperativaID === e.target.value || x.cooperativaID === Number(e.target.value)); setCoopSeleccionada(c || null); }}>
            {cooperativas.map(c => <MenuItem key={c.cooperativaID} value={c.cooperativaID}>{c.nombre} {c.siglas ? `(${c.siglas})` : ''}</MenuItem>)}
          </Select>
          {cooperativaIdFijo && <Typography variant="caption" color="text.secondary" sx={{ mt:0.5, ml:1.5 }}>Entidad asignada para este sitio</Typography>}
        </FormControl>
      </Box>

      {/* Fila 2: Nombre | Apellidos */}
      <Box sx={row}>
        <TextField sx={half} label={tc.s1.nombre} required size="small" value={form.nombre} onChange={e => sf('nombre', e.target.value)} error={!!errors.nombre} helperText={errors.nombre} />
        <TextField sx={half} label={tc.s1.apellidos} required size="small" value={form.apellidos} onChange={e => sf('apellidos', e.target.value)} error={!!errors.apellidos} helperText={errors.apellidos} />
      </Box>

      {/* Fila 3: Cédula | Expedida */}
      <Box sx={row}>
        <TextField sx={half} label={tc.s1.cedula} required size="small" value={form.cedula}
          onChange={e => { sf('cedula', e.target.value); setCedulaStatus('idle'); }}
          error={!!errors.cedula || cedulaStatus === 'taken'}
          helperText={errors.cedula ? errors.cedula : cedulaStatus === 'checking' ? tc.s1.cedVerificando : cedulaStatus === 'ok' ? tc.s1.cedOk : cedulaStatus === 'taken' ? tc.s1.cedTaken : ' '}
          InputProps={{ endAdornment: cedulaStatus === 'checking' ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment> : cedulaStatus === 'ok' ? <InputAdornment position="end"><CheckCircleOutlineIcon sx={{ color: C.darkGold, fontSize:18 }} /></InputAdornment> : null }} />
        <TextField sx={half} label={tc.s1.expedida} size="small" value={form.cedulaExpedida} onChange={e => sf('cedulaExpedida', e.target.value)} />
      </Box>

      {/* Cuentas existentes */}
      {cedulaStatus === 'ok' && cuentasExistentes.length > 0 && (
        <Box sx={{ mb: 2, p:2, borderRadius:2, border:`2px solid ${C.gold}`, background: C.cream }}>
          <Typography variant="body2" sx={{ fontWeight:700, color: C.darkGold, mb:0.5 }}>ℹ️ Esta cédula ya tiene cuenta en el sistema</Typography>
          <Typography variant="body2" sx={{ color:'#555', mb:1.5 }}>Estás registrado en otras entidades. ¿Deseas usar alguna o registrar una nueva?</Typography>
          <Box sx={{ display:'flex', flexDirection:'column', gap:1, mb:1.5 }}>
            {cuentasExistentes.map((c, i) => (
              <Button key={i} variant={cuentaElegida?.Email === c.email ? 'contained' : 'outlined'}
                onClick={() => { setCuentaElegida(c); sf('email', c.email); setEmailStatus('ok'); }}
                sx={{ justifyContent:'space-between', borderRadius:2, borderColor: C.gold, background: cuentaElegida?.Email === c.email ? C.black : 'transparent', color: cuentaElegida?.Email === c.email ? C.gold : C.black }}>
                <span>{c.email}</span>
                <Chip label={c.nombre} size="small" sx={{ fontSize:'0.65rem', background:'rgba(212,175,55,0.2)', color: C.black }}/>
              </Button>
            ))}
          </Box>
          <Button size="small" variant="text" sx={{ color:'#555', fontSize:'0.78rem' }} onClick={() => { setCuentaElegida({ nueva: true }); sf('email', ''); setEmailStatus('idle'); }}>+ Registrar con un correo diferente</Button>
        </Box>
      )}

      {/* Fila 4: Fecha | Género | Estado Civil */}
      <Box sx={row}>
        <Box sx={third}><DateField label={tc.s1.fechaNac} value={form.fechaNacimiento} onChange={v => sf('fechaNacimiento', v)} required maxYear={new Date().getFullYear() - 18} error={!!errors.fechaNac} helperText={errors.fechaNac || ' '} /></Box>
        <FormControl size="small" sx={third}>
          <InputLabel>{tc.s1.genero}</InputLabel>
          <Select value={form.genero} label="Género" onChange={e => sf('genero', e.target.value)}>
            <MenuItem value="M">{tc.s1.generos[0]}</MenuItem><MenuItem value="F">{tc.s1.generos[1]}</MenuItem><MenuItem value="NB">{tc.s1.generos[2]}</MenuItem><MenuItem value="O">{tc.s1.generos[3]}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={third} required error={!!errors.estadoCivil}>
          <InputLabel>{tc.s1.estadoCivil}</InputLabel>
          <Select value={form.estadoCivil} label="Estado Civil *" onChange={e => sf('estadoCivil', e.target.value)}>
            <MenuItem value="SOLTERO">{tc.s1.estadosCiviles[0]}</MenuItem><MenuItem value="CASADO">{tc.s1.estadosCiviles[1]}</MenuItem>
            <MenuItem value="DIVORCIADO">{tc.s1.estadosCiviles[2]}</MenuItem><MenuItem value="VIUDO">{tc.s1.estadosCiviles[3]}</MenuItem>
            <MenuItem value="UNION_LIBRE">{tc.s1.estadosCiviles[4]}</MenuItem><MenuItem value="OTRO">{tc.s1.estadosCiviles[5]}</MenuItem>
          </Select>
          {errors.estadoCivil && <Typography variant="caption" color="error" sx={{ ml:1.5, mt:0.3, display:'block' }}>{errors.estadoCivil}</Typography>}
        </FormControl>
      </Box>

      {/* Fila 5: Título ubicación */}
      <Typography variant="caption" sx={{ color: C.darkGold, fontWeight: 700, display: 'block', mb: 1 }}>Ubicación de residencia</Typography>

      {/* Fila 6: País | Departamento | Ciudad */}
      <Box sx={row}>
        <FormControl size="small" sx={third}>
          <InputLabel>{tc.s1.pais}</InputLabel>
          <Select value={form.paisId} label="País" onChange={e => { sf('paisId', e.target.value); sf('departamentoId', ''); sf('municipioId', ''); }}>
            {(paises.length ? paises : [{ PaisID: 1045, Nombre: 'Colombia' }]).map(p => <MenuItem key={p.PaisID||p.paisID} value={p.PaisID||p.paisID}>{p.Nombre||p.nombre}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={third} disabled={!form.paisId || loadingGeo}>
          <InputLabel>{loadingGeo ? tc.s1.cargando : tc.s1.departamento}</InputLabel>
          <Select value={form.departamentoId} label="Departamento" onChange={e => { sf('departamentoId', e.target.value); sf('municipioId', ''); }}>
            <MenuItem value=""><em>{tc.s1.seleccione}</em></MenuItem>
            {departamentos.map(d => <MenuItem key={d.DepartamentoID||d.departamentoID} value={d.DepartamentoID||d.departamentoID}>{d.Nombre||d.nombre}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={third} disabled={!form.departamentoId || loadingGeo}>
          <InputLabel>{loadingGeo ? tc.s1.cargando : tc.s1.ciudad}</InputLabel>
          <Select value={form.municipioId} label="Ciudad / Municipio" onChange={e => sf('municipioId', e.target.value)}>
            <MenuItem value=""><em>{tc.s1.seleccione}</em></MenuItem>
            {municipios.map(m => <MenuItem key={m.MunicipioID||m.municipioID} value={m.MunicipioID||m.municipioID}>{m.Nombre||m.nombre}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Fila 7: Dirección | Código Postal */}
      <Box sx={row}>
        <TextField sx={twoThird} label={tc.s1.direccion} size="small" value={form.direccion} onChange={e => sf('direccion', e.target.value)}
          InputProps={{ endAdornment: (<InputAdornment position="end"><Tooltip title="Obtener código postal automáticamente"><span><IconButton size="small" onClick={handleGeo} disabled={!form.direccion.trim() || geoLoading} sx={{ color: C.darkGold }}>{geoLoading ? <CircularProgress size={16} /> : <MyLocationIcon fontSize="small" />}</IconButton></span></Tooltip></InputAdornment>) }} />
        <TextField sx={third} label={tc.s1.codigoPostal} size="small" value={form.codigoPostal}
          onChange={e => { sf('codigoPostal', e.target.value); setGeoError(''); }}
          helperText={geoError || tc.s1.helperGeo}
          error={!!geoError && !form.codigoPostal}
          FormHelperTextProps={{ sx: { color: geoError ? '#c62828' : '#888' } }} />
      </Box>

      {/* Fila 8: Celular | Celular 2 | Email */}
      <Box sx={row}>
        <TextField sx={third} label={tc.s1.celular} required size="small" value={form.celular} onChange={e => sf('celular', e.target.value)} error={!!errors.celular} helperText={errors.celular || ' '} InputProps={{ startAdornment: <InputAdornment position="start">+57</InputAdornment> }} />
        <TextField sx={third} label={tc.s1.celular2} size="small" value={form.celular2} onChange={e => sf('celular2', e.target.value)} helperText=" " InputProps={{ startAdornment: <InputAdornment position="start">+57</InputAdornment> }} />
        <TextField sx={third} label={tc.s1.email} required size="small" type="email" value={form.email}
          onChange={e => { sf('email', e.target.value); setEmailStatus('idle'); }}
          error={!!errors.email || emailStatus === 'taken'}
          helperText={errors.email ? errors.email : emailStatus === 'checking' ? tc.s1.cedVerificando : emailStatus === 'ok' ? tc.s1.emailOk : emailStatus === 'taken' ? '❌ Ya registrado' : ' '}
          InputProps={{ endAdornment: emailStatus === 'checking' ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment> : null }} />
      </Box>

      {/* Fila 9: Código Alfa Numérico */}
      <Box sx={row}>
        <TextField sx={third} label={tc.s1.codigoAlfaNum} size="small" value={form.codigoAlfaNum} onChange={e => sf('codigoAlfaNum', e.target.value)} helperText=" " />
      </Box>
    </Box>
    );
  };

    // ── STEP 2 — Documentos ──────────────────────────────────────────────────
  const DOCS_S2_TIPOS = ['cedula','rut','antecedentes_procuraduria','antecedentes_contraloria','antecedentes_policia','bancario'];
  const DOCS_S2_ICONS = ['🪪','📄','⚖️','🏛️','👮','🏦'];
  const DOCS_S2_OBLIGATORIO = [true, false, false, false, false, false];
  const DOCS_S2 = DOCS_S2_TIPOS.map((tipo, i) => ({
    tipo,
    label: tc.s2.docs[i].label,
    obligatorio: DOCS_S2_OBLIGATORIO[i],
    icon: DOCS_S2_ICONS[i],
    desc: tc.s2.docs[i].desc,
  }));

  const S2 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700, mb: 0.5 }}>Documentos del Capellán</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
        Suba sus documentos para que el revisor evalúe su solicitud. La cédula es obligatoria para continuar.
      </Typography>
      <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
        📋 {tc.s2.alertaInfo}
      </Alert>
      <Box sx={{ mb: 2.5, p:1.5, borderRadius:2,
        background: urlDocIdentidad && todosDocsResueltos ? `rgba(212,175,55,0.08)` : '#fff8e1',
        border: `1px solid ${urlDocIdentidad && todosDocsResueltos ? C.gold : '#ffe082'}`,
        display:'flex', alignItems:'center', gap:1 }}>
        {urlDocIdentidad && todosDocsResueltos
          ? <><CheckCircleOutlineIcon sx={{ color: C.darkGold, fontSize:18 }}/><Typography variant="caption" sx={{ color: C.darkGold, fontWeight:700 }}>✅ Todos los documentos listos — puede continuar</Typography></>
          : <Typography variant="caption" sx={{ color:'#e65100', fontWeight:600 }}>
              {!urlDocIdentidad
                ? '❌ Cédula pendiente de verificación OCR'
                : `⚠️ Faltan ${['rut','antecedentes_procuraduria','antecedentes_contraloria','antecedentes_policia','bancario'].filter(t => !docResuelto(t)).length} documento(s)`}
            </Typography>
        }
      </Box>
      <Stepper activeStep={docsPaso} orientation="vertical">
        {DOCS_S2.map((doc, idx) => {
          const urlDoc = idx === 0 ? urlDocIdentidad : docsAdicionales[doc.tipo]?.url;
          const valido = idx === 0 ? !!urlDocIdentidad : !!docsAdicionales[doc.tipo]?.url;
          return (
            <Step key={doc.tipo} expanded={docsPaso === idx || valido}>
              <Box id={`doc-step-${idx}`} sx={{ position:'relative', top:-80 }}/>
              <StepLabel
                StepIconComponent={() => (
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: valido ? C.black : docsPaso === idx ? C.darkGrey : '#e8e8e8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: docsPaso === idx ? `0 0 0 3px rgba(212,175,55,0.3)` : 'none',
                    fontSize: 18, transition: 'all 0.3s',
                  }}>
                    {valido
                      ? <CheckCircleOutlineIcon sx={{ fontSize: 20, color: C.gold }}/>
                      : <span>{doc.icon}</span>}
                  </Box>
                )}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: docsPaso >= idx ? 700 : 400, color: docsPaso >= idx ? C.black : '#aaa', fontSize: '0.95rem' }}>
                    {doc.label}
                  </Typography>
                  {doc.obligatorio && <Chip label="Obligatorio" size="small" sx={{ height:18, fontSize:'0.62rem', fontWeight:700, background: C.black, color: C.gold }}/>}
                  {valido && <Chip label="Verificado ✓" size="small" sx={{ height:20, fontSize:'0.62rem', fontWeight:700, background:`rgba(212,175,55,0.15)`, color: C.darkGold }}/>}
                </Box>
              </StepLabel>
              <MuiStepContent>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>{doc.desc}</Typography>
                <VerificadorDocumento
                  titulo="" descripcion="Formatos aceptados: PDF, JPG, PNG. Máximo 10MB."
                  cedula={form.cedula} nombres={form.nombre} apellidos={form.apellidos}
                  tipoDocumento={doc.tipo.startsWith('antecedentes') ? 'antecedentes' : doc.tipo}
                  onValido={(file, resultado) => {
                    if (idx === 0) {
                      setDocIdentidad(file);
                      setUrlDocIdentidad(resultado?.urlDocumento || null);
                      if (resultado?.urlDocumento) setTimeout(() => { setDocsPaso(1); scrollToDoc(1); }, 800);
                    } else {
                      setDoc(doc.tipo, file, resultado?.urlDocumento || null);
                      if (resultado?.urlDocumento) setTimeout(() => { setDocsPaso(p => { const n = Math.min(5, p+1); scrollToDoc(n); return n; }); }, 800);
                    }
                  }}
                  onInvalido={() => { if (idx === 0) setDocIdentidad(null); else setDoc(doc.tipo, null, null); }}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {idx > 0 && <Button size="small" variant="text" onClick={() => setDocsPaso(p => Math.max(0, p - 1))} sx={{ color: '#888', fontSize: '0.75rem' }}>← Anterior</Button>}
                  {idx === 0 && valido && <Button size="small" variant="outlined" onClick={() => { setDocsPaso(1); scrollToDoc(1); }} sx={{ borderColor: C.gold, color: C.darkGold, fontSize:'0.75rem', borderRadius:4 }}>Siguiente →</Button>}
                  {idx > 0 && valido && idx < 5 && <Button size="small" variant="outlined" onClick={() => { setDocsPaso(p => { const n = Math.min(5, p+1); scrollToDoc(n); return n; }); }} sx={{ borderColor: C.gold, color: C.darkGold, fontSize:'0.75rem', borderRadius:4 }}>Siguiente →</Button>}
                  {idx > 0 && !valido && !docsPendientes[doc.tipo] && (
                    <Button size="small" variant="outlined" color="warning"
                      onClick={() => { if (window.confirm(`⚠️ ADVERTENCIA\n\nSi marca "${doc.label}" como pendiente, su solicitud quedará con documentos incompletos.\n\n¿Confirma marcar como pendiente?`)) { marcarPendiente(doc.tipo); if (idx < 5) setTimeout(() => { setDocsPaso(p => { const n = Math.min(5, p+1); scrollToDoc(n); return n; }); }, 300); } }}
                      sx={{ fontSize:'0.72rem', borderRadius:4 }}>
                      ⚠️ Marcar como pendiente
                    </Button>
                  )}
                  {idx > 0 && docsPendientes[doc.tipo] && !valido && (
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <Chip label="⚠️ Pendiente de entrega" size="small" sx={{ background:'#fff3e0', color:'#e65100', fontWeight:700, fontSize:'0.68rem' }}/>
                      <Button size="small" variant="text" onClick={() => desmarcarPendiente(doc.tipo)} sx={{ color:'#888', fontSize:'0.68rem', textDecoration:'underline' }}>Subir ahora</Button>
                      {idx < 5 && <Button size="small" variant="outlined" onClick={() => { setDocsPaso(p => { const n = Math.min(5, p+1); scrollToDoc(n); return n; }); }} sx={{ borderColor: C.gold, color: C.darkGold, fontSize:'0.72rem', borderRadius:4 }}>Siguiente →</Button>}
                    </Box>
                  )}
                </Box>
              </MuiStepContent>
            </Step>
          );
        })}
      </Stepper>
      <Box sx={{ mt:2, p:1.5, borderRadius:2, background:'#f5f5f5', display:'flex', gap:1, alignItems:'center' }}>
        <LockIcon sx={{ fontSize:16, color:'#888' }}/>
        <Typography variant="caption" sx={{ color:'#666' }}>{tc.s2.privacidad}</Typography>
      </Box>
    </Box>
  );

  // ── STEP 3 — Beneficiarios ───────────────────────────────────────────────
  const S3 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700, mb: 0.5 }}>Declaración de Beneficiarios</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>En caso de fallecimiento, estas personas dispondrán del saldo en partes iguales.</Typography>
      <ReadSection title={{ number: 'CLÁUSULA 3ª', title: tc.s3.title }} onRead={() => rc('incorp')} read={!!form.readClauses['incorp']} btnLeer={tc.s3.btnLeer} btnLeido={tc.s3.btnLeido}>
        <SectionText>{tc.s3.p1}</SectionText>
        <SectionText>{tc.s3.p2}</SectionText>
        <SectionText>{tc.s3.p3}</SectionText>
      </ReadSection>
      <ClausulaTitle number="CLÁUSULA 4ª" title="Beneficiarios de la Cuenta del Capellán" />
      <Typography variant="caption" sx={{ color: '#666', mb: 2, display: 'block' }}>Puede dejar en blanco los que no apliquen.</Typography>
      {form.beneficiarios.map((b, i) => (
        <Paper key={i} elevation={0} sx={{ border: `1px solid ${C.lightGold}`, borderRadius: 2, p: 2, mb: 2, background: C.cream }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: C.darkGold, mb: 1.5, display: 'block' }}>Beneficiario {i + 1}</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}><TextField fullWidth label={tc.s3.nombres} size="small" value={b.nombres} onChange={e => sb(i, 'nombres', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label={tc.s1.apellidos} size="small" value={b.apellidos} onChange={e => sb(i, 'apellidos', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label={tc.s3.cedulaBen} size="small" value={b.cedula} onChange={e => sb(i, 'cedula', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label={tc.s3.contacto} size="small" value={b.contacto} onChange={e => sb(i, 'contacto', e.target.value)} /></Grid>
          </Grid>
        </Paper>
      ))}
    </Box>
  );

  // ── STEP 4 — Aportes ─────────────────────────────────────────────────────
  const S4 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700, mb: 0.5 }}>Cuota de Inscripción y Aportes</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Defina su modalidad de aporte y las condiciones económicas de su inscripción.</Typography>
      <ReadSection title={tc.s4.clausula5} onRead={() => rc('obj5')} read={!!form.readClauses['obj5']} btnLeer={tc.s4.btnLeer} btnLeido={tc.s4.btnLeido}>
        <SectionText>{tc.s4.p5}</SectionText>
      </ReadSection>
      <ClausulaTitle number="CLÁUSULA 6ª" title="Formato de Aporte" />
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 2, background: C.offWhite }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>Inscripción a la Alianza de Capellanes</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>Pago único — cuota de inscripción</Typography>
          </Box>
          <Chip label="$60.000 COP" size="small" sx={{ fontWeight:700, background: C.black, color: C.gold }} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>Afiliación red de Consumo Colaborativo</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>Plataforma digital de economías colaborativas</Typography>
          </Box>
          <Chip label="$60.000 COP" size="small" sx={{ fontWeight:700, background: C.black, color: C.gold }} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>Aporte mínimo mensual</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>Consumo colaborativo en establecimientos autorizados por la red solidaria</Typography>
          </Box>
          <Chip label="$220.000 COP/mes" variant="outlined" size="small" sx={{ fontWeight:700, borderColor: C.gold, color: C.darkGold }} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <FormLabel component="legend" sx={{ fontSize: '0.8rem', fontWeight: 600, color: C.darkGold, mb: 1 }}>Modalidad de pago del aporte mensual:</FormLabel>
          <RadioGroup value={form.modalidadAporte} onChange={e => sf('modalidadAporte', e.target.value)}>
            {['TRABAJO','BIENES','DINERO'].map((val, i) => (
              <FormControlLabel key={val} value={val} control={<Radio size="small" sx={{ color: C.gold, '&.Mui-checked': { color: C.darkGold } }} />}
                label={<Typography variant="body2"><strong>{tc.s4.modalidades[i].label}</strong> — {tc.s4.modalidades[i].desc}</Typography>} />
            ))}
          </RadioGroup>
        </FormControl>
        {form.modalidadAporte === 'BIENES' && (
          <TextField fullWidth label={tc.s4.valorBienes} size="small" type="number" value={form.valorBienes} onChange={e => sf('valorBienes', e.target.value)} sx={{ mt: 2 }} />
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#444', display: 'block', mb: 1.5 }}>Fecha estimada de primera cuota:</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <DateField label={tc.s4.fechaPrimeraCuota} value={form.fechaPrimeraCuota} onChange={v => sf('fechaPrimeraCuota', v)} minYear={new Date().getFullYear()} maxYear={new Date().getFullYear() + 2} />
          </Grid>
        </Grid>
        <Alert severity="info" icon={false} sx={{ mt: 2, fontSize: '0.82rem', background: C.cream, border: `1px solid ${C.lightGold}`, borderRadius: 2 }}>
          💳 {tc.s4.alertaPago}
        </Alert>
      </Paper>
    </Box>
  );

  // ── STEP 5 — Obligaciones ────────────────────────────────────────────────
  const S5 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700, mb: 0.5 }}>Obligaciones y Términos</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Lea y acepte cada una de las siguientes cláusulas.</Typography>
      <ReadSection title={tc.s5.clausula7} onRead={() => rc('ob7')} read={!!form.readClauses['ob7']} btnLeer={tc.s5.btnLeer} btnLeido={tc.s5.btnLeido}>
        <SectionText>{tc.s5.p7}</SectionText>
      </ReadSection>
      <ReadSection title={tc.s5.clausula8} onRead={() => rc('ob8')} read={!!form.readClauses['ob8']} btnLeer={tc.s5.btnLeer} btnLeido={tc.s5.btnLeido}>
        <SectionText>{tc.s5.p8}</SectionText>
      </ReadSection>
      <ReadSection title={tc.s5.clausula9} onRead={() => rc('ob9')} read={!!form.readClauses['ob9']} btnLeer={tc.s5.btnLeer} btnLeido={tc.s5.btnLeido}>
        <SectionText>{tc.s5.p9}</SectionText>
      </ReadSection>
      {errors.rc && <Alert severity="warning">{errors.rc}</Alert>}
    </Box>
  );

  // ── STEP 6 — Confidencialidad ────────────────────────────────────────────
  const S6 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700, mb: 0.5 }}>Propiedad Intelectual y Confidencialidad</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Cláusulas sobre propiedad intelectual, derechos de autor y reserva.</Typography>
      <ReadSection title={tc.s6.clausula1011} onRead={() => rc('prop')} read={!!form.readClauses['prop']} btnLeer={tc.s6.btnLeer} btnLeido={tc.s6.btnLeido}>
        <SectionText>{tc.s6.p1011}</SectionText>
      </ReadSection>
      <ReadSection title={tc.s6.clausula12} onRead={() => rc('conf')} read={!!form.readClauses['conf']} btnLeer={tc.s6.btnLeer} btnLeido={tc.s6.btnLeido}>
        <SectionText>{tc.s6.p12}</SectionText>
      </ReadSection>
      {errors.rc && <Alert severity="warning">{errors.rc}</Alert>}
    </Box>
  );

  // ── STEP 7 — Firma ───────────────────────────────────────────────────────
  const S7 = () => (
    <Box>
      <Typography variant="h6" sx={{ color: C.black, fontWeight: 700, mb: 0.5 }}>Firma y Consentimiento</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Cláusula 14ª – Acuerdo Total · Cláusula 16ª – Consentimiento.</Typography>
      <Paper elevation={0} sx={{ border: `1px solid ${C.lightGold}`, borderRadius: 2, p: 2.5, mb: 3, background: C.cream }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.black, mb: 1.5 }}>Resumen de su inscripción:</Typography>
        <Grid container spacing={1}>
          {[['Nombre completo', `${form.nombre} ${form.apellidos}`], ['Cédula', form.cedula], ['Celular', form.celular], ['Email', form.email], ['Estado civil', form.estadoCivil], ['Modalidad de aporte', form.modalidadAporte]].map(([l, v]) => (
            <React.Fragment key={l}>
              <Grid item xs={5}><Typography variant="caption" sx={{ fontWeight: 600 }}>{l}:</Typography></Grid>
              <Grid item xs={7}><Typography variant="caption">{v || '—'}</Typography></Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>
      {[
        { f: 'aceptaTerminos',       ek: 'at', lbl: '{tc.s7.check1}' },
        { f: 'declaracionVeracidad', ek: 'dv', lbl: '{tc.s7.check2}' },
        { f: 'autorizaDatos',        ek: 'ad', lbl: '{tc.s7.check3}' },
      ].map(({ f, ek, lbl }) => (
        <Paper key={f} elevation={0} onClick={() => sf(f, !form[f])}
          sx={{ border: form[f] ? `1.5px solid ${C.gold}` : '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 1.5, cursor: 'pointer', background: form[f] ? `rgba(212,175,55,0.06)` : '#fff', transition: 'all 0.2s' }}>
          <FormControlLabel
            control={<Checkbox checked={form[f]} onChange={e => sf(f, e.target.checked)} onClick={e => e.stopPropagation()} sx={{ color: C.gold, '&.Mui-checked': { color: C.darkGold } }} />}
            label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{lbl}</Typography>} />
          {errors[ek] && <Typography variant="caption" color="error" sx={{ ml: 4 }}>Requerido</Typography>}
        </Paper>
      ))}
      {/* Captcha */}
      <Paper elevation={0} sx={{ border: errors.captcha ? '1.5px solid #d32f2f' : `1px solid ${C.lightGold}`, borderRadius: 2, p: 2.5, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#444', letterSpacing: '0.04em' }}>VERIFICACIÓN DE SEGURIDAD</Typography>
          <Tooltip title="Nueva pregunta">
            <IconButton size="small" onClick={() => { setCaptcha(newCaptcha()); sf('captchaResp', ''); }} sx={{ color: C.darkGold }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, px: 3, py: 1.5, borderRadius: 2, minWidth: 160 }}>
            <Typography sx={{ color: C.gold, fontWeight: 800, fontSize: '1.4rem', letterSpacing: '0.1em', textAlign: 'center', fontFamily: 'monospace' }}>
              {captcha.a} {captcha.op} {captcha.b} = ?
            </Typography>
          </Box>
          <TextField label="Su respuesta" size="small" type="number" value={form.captchaResp}
            onChange={e => sf('captchaResp', e.target.value)} error={!!errors.captcha} sx={{ width: 130 }}
            inputProps={{ style: { textAlign: 'center', fontWeight: 700, fontSize: '1.1rem' } }} />
        </Box>
        {errors.captcha && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>Respuesta incorrecta. Intente de nuevo.</Typography>}
      </Paper>
      {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
    </Box>
  );

  const steps = [S0, S1, S2, S3, S4, S5, S6, S7];
  const geoParaPDF = {
    pais: (paises.find(p => (p.PaisID||p.paisID) === form.paisId)?.Nombre) || 'Colombia',
    departamento: (departamentos.find(d => (d.DepartamentoID||d.departamentoID) === form.departamentoId)?.Nombre) || '',
    municipio: (municipios.find(m => (m.MunicipioID||m.municipioID) === form.municipioId)?.Nombre) || '',
  };

  // ── Pantalla de éxito ────────────────────────────────────────────────────
  if (submitted) return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ height: 5, background: `linear-gradient(90deg, ${C.black}, ${C.darkGold}, ${C.gold})` }} />
      <DialogContent sx={{ textAlign: 'center', py: 4, px: 4 }}>
        <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
          <CheckCircleOutlineIcon sx={{ color: C.gold, fontSize: 42 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: C.black, mb: 1 }}>¡Solicitud enviada!</Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>Su solicitud fue recibida exitosamente.</Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>{tc.exito.emailMsg.replace('{email}', form.email)}</Typography>
        <Chip label="Estado: PENDIENTE DE VALIDACIÓN" sx={{ mb: 3, fontWeight: 700, background: C.lightGold, color: C.darkGold }} />

        <Box sx={{ background: C.cream, border: `1.5px solid ${C.lightGold}`, borderRadius: 2, p: 2.5, mb: 2.5, textAlign: 'left' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.black, mb: 0.8 }}>📄 Contrato de inscripción</Typography>
          <Typography variant="body2" sx={{ color: '#555', fontSize: '0.8rem', mb: 1.8 }}>
            {tc.exito.contratoDesc}{' '}
            <strong>fondoemprendimientofenagro@gmail.com</strong> o a{' '}
            <strong>alianzasolidaria@gmail.com</strong>
          </Typography>
          <Button fullWidth variant="contained" startIcon={pdfCargando ? <CircularProgress size={16} color="inherit"/> : <PictureAsPdfIcon />}
            disabled={pdfCargando}
            onClick={async () => {
              setPdfCargando(true);
              try { await descargarContratoPDF(form, afiliadoId, geoParaPDF, hashContrato, entidadPDF); }
              finally { setPdfCargando(false); }
            }}
            sx={{ background: `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`, borderRadius: 2, fontWeight: 700, py: 1.1, mb: 1, color: C.gold, '&:hover': { background: C.darkGrey } }}>
            {pdfCargando ? tc.exito.btnGenerando : tc.exito.btnDescargar}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', color: '#999', mt: 1, textAlign: 'center' }}>
            No. de solicitud: <strong>#{afiliadoId ? String(afiliadoId).padStart(6,'0') : tc.exito.asignado}</strong>
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 6, px: 4, borderColor: C.gold, color: C.darkGold, '&:hover': { background: `rgba(212,175,55,0.08)` } }}>
          Cerrar
        </Button>
      </DialogContent>
    </Dialog>
  );

  // ── Dialog principal ─────────────────────────────────────────────────────
  const StepContent = steps[step];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper"
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ flexShrink: 0 }}>
        <LinearProgress variant="determinate" value={((step + 1) / STEPS.length) * 100}
          sx={{ height: 5, '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${C.black}, ${C.darkGold})` }, background: C.lightGold }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
          <Box>
            <Typography variant="caption" sx={{ color: C.darkGold, fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.7rem' }}>
              {tc.header} — {tc.paso} {step + 1} {tc.de} {STEPS.length}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.black, mt: 0.2 }}>{STEPS[step].label}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#999', fontWeight: 600 }}>{Math.round(((step + 1) / STEPS.length) * 100)}%</Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: '#999' }}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
        {/* Barra de pasos */}
        <Box sx={{ display: 'flex', px: 3, py: 1, gap: 0.5, background: C.offWhite, borderBottom: '1px solid #f0f0f0', overflowX: 'auto' }}>
          {STEPS.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 4,
              background: i === step ? `rgba(212,175,55,0.15)` : 'transparent',
              border: i === step ? `1px solid ${C.gold}` : '1px solid transparent', flexShrink: 0 }}>
              {i < step
                ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: C.darkGold }} />
                : <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: i === step ? C.gold : '#ccc' }} />}
              <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: i === step ? 700 : 400, color: i === step ? C.darkGold : i < step ? C.gold : '#999', whiteSpace: 'nowrap' }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <DialogContent ref={contentRef} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 4 }, py: 3 }}>
        <Fade in key={step} timeout={300}><Box>{steps[step]()}</Box></Fade>
      </DialogContent>

      <Box sx={{ flexShrink: 0, px: { xs: 2, sm: 4 }, py: 2, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} disabled={step === 0 || submitting} sx={{ color: '#666', '&:hover': { color: C.darkGold } }}>Anterior</Button>
        <Typography variant="caption" sx={{ color: '#bbb' }}>{step + 1} / {STEPS.length}</Typography>
        {(() => {
          const bloqueado = submitting || !stepListo;
          return (
            <Button variant="contained"
              disabled={bloqueado}
              endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : step < 7 ? <ArrowForwardIcon /> : <DrawIcon />}
              onClick={handleNext}
              sx={{
                borderRadius: 6, px: 3,
                background: bloqueado ? '#bdbdbd' : `linear-gradient(135deg, ${C.black}, ${C.darkGrey})`,
                color: bloqueado ? '#fff' : C.gold,
                '&:hover': { background: bloqueado ? '#bdbdbd' : C.darkGrey },
                cursor: bloqueado ? 'not-allowed' : 'pointer',
              }}>
              {submitting ? tc.btnEnviando : step < 7 ? tc.btnSiguiente : tc.btnFirmar}
            </Button>
          );
        })()}
      </Box>
    </Dialog>
  );
}
